<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { useNotificationsQuery, type NotificationItem } from '~/composables/useNotificationsQuery';
import { useMarkNotificationRead } from '~/api/mutations/useMarkNotificationRead';
import { useMarkAllNotificationsRead } from '~/api/mutations/useMarkAllNotificationsRead';

const { t } = useI18n();
const router = useRouter();

const { data: notifications } = useNotificationsQuery();
const { mutate: markOneRead } = useMarkNotificationRead();
const { mutate: markAllRead } = useMarkAllNotificationsRead();

const items = computed<NotificationItem[]>(() => notifications.value?.items?.slice(0, 10) ?? []);
const unreadCount = computed<number>(() => notifications.value?.unreadCount ?? 0);
const badgeLabel = computed<string>(() => (unreadCount.value > 99 ? '99+' : String(unreadCount.value)));

const isOpen = ref(false);
const triggerRef = ref<HTMLButtonElement | null>(null);
const panelRef = ref<HTMLDivElement | null>(null);

const togglePanel = () => {
  isOpen.value = !isOpen.value;
};

const closePanel = () => {
  isOpen.value = false;
};

const onMarkAllRead = () => {
  if (unreadCount.value === 0) return;
  markAllRead();
};

const onItemClick = async (item: NotificationItem) => {
  if (!item.readAt) markOneRead(item.id);
  closePanel();
  if (item.href) {
    await router.push(item.href);
  }
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    closePanel();
    triggerRef.value?.focus();
  }
};

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKeydown);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', onKeydown);
  }
});

onClickOutside(
  panelRef,
  () => {
    if (isOpen.value) closePanel();
  },
  { ignore: [triggerRef] },
);

const categoryIcon = (category: NotificationItem['category']): string => {
  switch (category) {
    case 'DEAL_UPDATES':
      return 'check-circle-filled';
    case 'MARKET_NEWS':
      return 'world';
    case 'PROMOTIONS':
      return 'discount';
    case 'SYSTEM_ALERTS':
    default:
      return 'info-circle';
  }
};

const formatTimeAgo = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 45) return t('shell.notificationBell.time.justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('shell.notificationBell.time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('shell.notificationBell.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('shell.notificationBell.time.daysAgo', { count: days });
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return t('shell.notificationBell.time.weeksAgo', { count: weeks });
  const months = Math.floor(days / 30);
  if (months < 12) return t('shell.notificationBell.time.monthsAgo', { count: months });
  const years = Math.floor(days / 365);
  return t('shell.notificationBell.time.yearsAgo', { count: years });
};
</script>

<template>
  <div class="relative">
    <button
      ref="triggerRef"
      type="button"
      class="relative inline-flex h-[38px] w-[38px] items-center justify-center rounded-[10px] text-black-60 transition-colors hover:bg-black-05 hover:text-black-100"
      :aria-label="
        unreadCount > 0
          ? t('shell.notificationBell.ariaUnread', { count: unreadCount })
          : t('shell.notificationBell.title')
      "
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      @click="togglePanel"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <span
        v-if="unreadCount > 0"
        class="absolute -end-0.5 -top-0.5 inline-flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-coral-alert px-1 font-mono text-[9px] font-semibold leading-none text-white ring-2 ring-white"
      >
        {{ badgeLabel }}
      </span>
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="isOpen"
        ref="panelRef"
        role="dialog"
        :aria-label="t('shell.notificationBell.title')"
        class="absolute end-0 top-[calc(100%+8px)] z-50 w-[360px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-floating"
      >
        <header class="flex items-center justify-between border-b border-black/[0.08] px-5 py-4">
          <h3 class="font-serif text-[16px] font-semibold text-black-100">{{ t('shell.notificationBell.title') }}</h3>
          <button
            v-if="unreadCount > 0"
            type="button"
            class="font-mono text-[10px] uppercase tracking-[0.12em] text-black-60 transition-colors hover:text-black-100"
            @click="onMarkAllRead"
          >
            {{ t('shell.notificationBell.markAllRead') }}
          </button>
        </header>

        <div class="max-h-[480px] overflow-y-auto">
          <div
            v-if="items.length === 0"
            class="p-2"
          >
            <PortalUiEmptyState
              :title="t('shell.notificationBell.emptyTitle')"
              :description="t('shell.notificationBell.emptyDescription')"
            />
          </div>
          <ul
            v-else
            class="divide-y divide-black/[0.06]"
          >
            <li
              v-for="item in items"
              :key="item.id"
            >
              <button
                type="button"
                class="flex w-full items-start gap-3 px-5 py-3.5 text-start transition-colors hover:bg-black-03"
                :class="!item.readAt ? 'bg-gold-primary-10/40' : ''"
                @click="onItemClick(item)"
              >
                <span
                  class="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-black-03 text-black-80"
                  aria-hidden="true"
                >
                  <UIIcon
                    :name="categoryIcon(item.category)"
                    size="sm"
                  />
                </span>
                <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span class="flex items-baseline justify-between gap-2">
                    <span
                      class="truncate text-[13px] font-semibold text-black-100"
                      :class="!item.readAt ? '' : 'text-black-80'"
                    >
                      {{ item.title }}
                    </span>
                    <span class="flex-shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-black-50">
                      {{ formatTimeAgo(item.createdAt) }}
                    </span>
                  </span>
                  <span class="truncate text-[12px] leading-snug text-black-60">
                    {{ item.body }}
                  </span>
                </span>
                <span
                  v-if="!item.readAt"
                  class="ms-1 mt-2 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-coral-alert"
                  aria-hidden="true"
                />
              </button>
            </li>
          </ul>
        </div>

        <footer
          v-if="unreadCount > 0 || items.length > 0"
          class="flex items-center justify-end border-t border-black/[0.08] px-5 py-3"
        >
          <button
            type="button"
            class="font-mono text-[10px] uppercase tracking-[0.12em] text-black-60 transition-colors hover:text-black-100 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="unreadCount === 0"
            @click="onMarkAllRead"
          >
            {{ t('shell.notificationBell.markAllAsRead') }}
          </button>
        </footer>
      </div>
    </Transition>
  </div>
</template>
